import React, { useEffect, useState, useRef } from "react";
//** MUI Components */
import { FormControl, Typography, Paper, Stack } from "@mui/material";

//** ndbx Components */
// import { FormField } from "../../particles/formFieldRHF";
// import { Section } from "../../particles/layout/section";
// import { RHFTextField } from "../../molecules/rhfTextField";
// import { OneColumnStack } from "../../particles/layout/stack";

import {
  FormField,
  Section,
  // RHFTextField,
  OneColumnStack,
} from "@arp/react-ndbx";

import { RHFTextField } from "../../coding/rhfTextField";

//** Config */
import { paymentDetailsFieldNames } from "../../../config/pageInfo/pageFieldName";

//** Application components */
import ToggleButtonNotification from "../../organisms/ToggleButtonNotification.jsx";

//** Libraries  */
import { IMaskMixin } from "react-imask";

//** Hooks */
import { useGetBankNameByBsb } from "../../../hooks/dataHooks/useGetBankNameByBsb.js";

//** Context */
import usePreQualification from "../../../hooks/usePreQualification";
import useSharedData from "../../../hooks/useSharedData.ts";

//----------------------------------------------------------------

const RHFBankAccDetails = React.forwardRef((props, ref) => (
  <FormField ref={ref} Component={RHFBankAccDetailsImpl} {...props} />
));

const RHFBankAccDetailsImpl = React.forwardRef(
  (
    {
      name = "",
      variant = "standard",
      className,
      size,
      defaultValue,
      onChange,
      onBlur,
      onFocus,
      error,
      placeholder = "",
      label,
      helperText,
      showErrorCard = true,
      extendedError = { title: null, additionalText: null },
      dataSet,
      resetAll = false,
      onBsbChange,
    },
    ref
  ) => {
    //----------------------------------------------------------------

    useEffect(() => {}, [resetAll]);

    const BSBInputRef = React.useRef(null);
    const accountNumberInputRef = React.useRef(null);

    const { handleInputChange } = usePreQualification();

    useEffect(() => {
      const bsbMask = IMask(BSBInputRef.current, {
        mask: [
          {
            mask: "000-000",
            blocks: {
              start: {
                mask: IMask.MaskedRange,
                from: 0o0,
                to: 999,
              },
              end: {
                mask: IMask.MaskedRange,
                from: 0o0,
                to: 999,
              },
            },
            lazy: true,
          },
        ],
      });
      const accountNumberMask = IMask(accountNumberInputRef.current, {
        mask: [
          {
            mask: "0000 0000 00",
            pattern: /^\d{6,9}$/,
            lazy: true,
          },
        ],
      });
      return () => {
        bsbMask.destroy();
        accountNumberMask.destroy();
      };
    }, []);

    //----------------------------------------------------------------

    const { accessToken } = useSharedData();

    const [showBSBError, setShowBSBError] = useState(false);
    const [bsbError, setBsbError] = useState("");
    const [nameOfFinancialInstitution, setNameOfFinancialInstitution] =
      useState("");
    const [bsbNumber, setBsbNumber] = useState(null);

    const { data, refetch, isFetching, isLoading } = useGetBankNameByBsb({
      bsbNumber: bsbNumber,
      accessToken: accessToken,
    });

    function resetNameOfFinancialInstitution(bankNameValue) {
      handleInputChange({
        name: "bankNameOfFinancialInstitution",
        value: bankNameValue,
      });
      setNameOfFinancialInstitution(bankNameValue);
      onBsbChange(!bankNameValue ? true : false);
    }

    const handleOnBlurBSB = (e) => {
      e.preventDefault();
      const { value } = { ...e.target };

      if (!value) {
        setShowBSBError(true);
        setBsbError("The BSB number is required");
        resetNameOfFinancialInstitution("");
        return;
      }

      //* Remove the dash to allow us to ensure the correct bsb length
      let formatBSB = value?.toString().replace("-", "");

      if (formatBSB.length < 6) {
        setShowBSBError(true);
        setBsbError("The BSB number must be 6 digits");
        resetNameOfFinancialInstitution("");
        return;
      }
      setBsbNumber(formatBSB);
      refetch({
        bsbNumber: bsbNumber,
        accessToken: accessToken,
      });
      onBsbChange(data?.data?.bankName ? false : true);
    };

    useEffect(() => {
      if (!data) return;
      const status = data?.response?.status ?? data?.status;

      if (status !== 200) {
        setShowBSBError(true);
        setBsbError(
          status === 404
            ? "The BSB number you've entered doesn't match with a known BSB. Check and re-enter your BSB number."
            : "There was an issue retrieving the Financial Institution details"
        );
        //TODO: MANUALLY UPDATE THE CONTEXT STATE
        resetNameOfFinancialInstitution("");
        return;
      }

      setShowBSBError(false);
      setBsbError("");
      //TODO: MANUALLY UPDATE THE CONTEXT STATE
      resetNameOfFinancialInstitution(data?.data?.bankName || "");
    }, [data]);
    //----------------------------------------------------------------

    // BANK_PAYMENT_PERMISSION_GRANTED: "bankPaymentPermissionGranted",
    return (
      <FormControl sx={{ width: "100%" }} size={size} error={error}>
        <Paper elevation={0}>
          <OneColumnStack name="bank-account-details" maxWidth={"100%"}>
            <Section>
              <RHFTextField
                className="subvariant-text-input"
                variant="standard"
                label="BSB"
                controlIdentifier="bank-bsb"
                inputRef={BSBInputRef}
                name={paymentDetailsFieldNames().BANK_BSB}
                sx={{ width: "100%" }}
                defaultValue={
                  dataSet != null && resetAll === false ? dataSet.bankBSB : ""
                }
                onBlur={handleOnBlurBSB}
                error={showBSBError}
                helperText={showBSBError === true ? bsbError : ""}
                disablePaste={true}
              />
            </Section>
            <Section>
              <RHFTextField
                className="subvariant-text-input"
                variant="standard"
                label="Account number"
                controlIdentifier="bank-account-number"
                placeholder={"0000 0000 00"}
                inputRef={accountNumberInputRef}
                name={paymentDetailsFieldNames().BANK_ACCOUNT_NUMBER}
                sx={{ width: "100%" }}
                defaultValue={
                  dataSet != null && resetAll === false
                    ? dataSet.bankAccountNumber
                    : ""
                }
                disablePaste={true}
              />
            </Section>
            <Section>
              <RHFTextField
                className="subvariant-text-input"
                variant="standard"
                label="Account name"
                controlIdentifier="bank-account-name"
                name={paymentDetailsFieldNames().BANK_ACCOUNT_NAME}
                sx={{ width: "100%" }}
                defaultValue={
                  dataSet != null && resetAll === false
                    ? dataSet.bankAccountName
                    : ""
                }
              />
            </Section>
            <Section>
              <RHFTextField
                className="subvariant-text-input"
                variant="standard"
                label="Name of financial institution"
                controlIdentifier="name-of-financial-institution"
                name={
                  paymentDetailsFieldNames().BANK_NAME_OF_FINANCIAL_INSTITUTION
                }
                sx={{ width: "100%" }}
                value={nameOfFinancialInstitution}
                defaultValue={
                  dataSet != null && resetAll === false
                    ? dataSet.bankNameOfFinancialInstitution
                    : ""
                }
                readOnly={true}
                // ref={inputRefNameOfFinancialInstitution}
              />
            </Section>
          </OneColumnStack>
        </Paper>
      </FormControl>
    );
  }
);

export default RHFBankAccDetails;
